import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "content", "loginDialog"]

  static values = {
    url: String,
    entryLang: String,
    loginUrl: String,
    fingerprint: String
  }

  connect() {
    this.originalHtml = null
    this.translated = false
    this.detectAndShow()

    // Backdrop click to close
    if (this.hasLoginDialogTarget) {
      this.loginDialogTarget.addEventListener("click", (e) => {
        if (e.target === this.loginDialogTarget) {
          this.loginDialogTarget.close()
        }
      })
    }
  }

  detectAndShow() {
    if (!this.hasButtonTarget || !this.entryLangValue) return

    const browserLang = navigator.language.split("-")[0]
    const entryLang = this.entryLangValue.split("-")[0]

    if (browserLang !== entryLang) {
      this.targetLangName = this.getLanguageName(navigator.language)
      this.targetLangCode = navigator.language
      this.buttonTarget.textContent = this.translateLabel()
      this.buttonTarget.hidden = false
    }
  }

  async translate() {
    // Not logged in — show login dialog
    if (!this.hasContentTarget && this.hasLoginDialogTarget) {
      this.loginDialogTarget.showModal()
      return
    }

    if (this.translated) {
      this.restore()
      return
    }

    if (!this.hasContentTarget) return

    // Save original
    this.originalHtml = this.contentTarget.innerHTML

    // Check sessionStorage cache
    const cacheKey = `til:tr:${this.urlValue}:${this.targetLangCode}:${this.fingerprintValue || "_"}`
    const cached = sessionStorage.getItem(cacheKey)
    if (cached) {
      this.contentTarget.innerHTML = cached
      this.translated = true
      this.buttonTarget.textContent = this.buttonTarget.dataset.seeOriginalText || "See original"
      return
    }

    // Loading state
    this.buttonTarget.disabled = true
    this.buttonTarget.textContent = this.buttonTarget.dataset.translatingText || "Translating..."

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ target_lang: this.targetLangCode })
      })

      if (response.status === 429) {
        const data = await response.json()
        this.showError(data.message || "Daily limit reached")
        return
      }

      if (!response.ok) {
        this.showError(this.buttonTarget.dataset.errorText || "Translation failed")
        return
      }

      const data = await response.json()
      this.contentTarget.innerHTML = data.html
      this.translated = true
      this.buttonTarget.textContent = this.buttonTarget.dataset.seeOriginalText || "See original"

      // Store in sessionStorage
      try { sessionStorage.setItem(cacheKey, data.html) } catch {}
    } catch (e) {
      this.showError(this.buttonTarget.dataset.errorText || "Translation failed")
    } finally {
      this.buttonTarget.disabled = false
    }
  }

  restore() {
    if (this.originalHtml && this.hasContentTarget) {
      this.contentTarget.innerHTML = this.originalHtml
      this.translated = false
      this.buttonTarget.textContent = this.translateLabel()
    }
  }

  closeLogin() {
    if (this.hasLoginDialogTarget) {
      this.loginDialogTarget.close()
    }
  }

  translateLabel() {
    const template = this.buttonTarget.dataset.translateText || "Translate to %{lang}"
    return template.replace("%{lang}", this.targetLangName)
  }

  getLanguageName(langCode) {
    try {
      const names = new Intl.DisplayNames([langCode], { type: "language" })
      return names.of(langCode)
    } catch {
      return langCode
    }
  }

  showError(message) {
    this.buttonTarget.textContent = message
    this.buttonTarget.disabled = true
    setTimeout(() => {
      this.buttonTarget.disabled = false
      this.buttonTarget.textContent = this.translateLabel()
    }, 3000)
  }
}
